import type { ComponentProps, ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface CardProps extends Omit<ComponentProps<'section'>, 'title'> {
  /** Card heading (rendered as h2 by default). */
  title?: ReactNode;
  headingLevel?: 2 | 3 | 4;
  description?: ReactNode;
  /** Buttons/links shown at the right of the heading. */
  actions?: ReactNode;
  footer?: ReactNode;
  padding?: 'none' | 'sm' | 'md';
  tone?: 'default' | 'muted' | 'primary';
}

const paddings = { none: '', sm: 'p-3', md: 'p-4 sm:p-5' };
const tones = {
  default: 'bg-surface border-border',
  muted: 'bg-surface-muted border-border',
  primary: 'bg-primary-soft border-primary',
};

export function Card({
  title,
  headingLevel = 2,
  description,
  actions,
  footer,
  padding = 'md',
  tone = 'default',
  className,
  children,
  ...rest
}: CardProps) {
  const H = `h${headingLevel}` as 'h2' | 'h3' | 'h4';
  return (
    <section className={cn('rounded-[var(--radius-card)] border shadow-card', tones[tone], paddings[padding], className)} {...rest}>
      {(title || actions) && (
        <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            {title && <H className={cn(headingLevel === 2 ? 'text-xl' : 'text-lg')}>{title}</H>}
            {description && <p className="mt-1 text-muted">{description}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      {children}
      {footer && <div className="mt-4 border-t border-border pt-3">{footer}</div>}
    </section>
  );
}

export interface LinkCardProps {
  to: string;
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  /** Extra line under the description, e.g. a badge. */
  meta?: ReactNode;
  headingLevel?: 2 | 3;
  className?: string;
}

/** Whole-card link used on section landing pages and quick actions. */
export function LinkCard({ to, title, description, icon, meta, headingLevel = 2, className }: LinkCardProps) {
  const H = `h${headingLevel}` as 'h2' | 'h3';
  return (
    <RouterLink
      to={to}
      className={cn(
        'group flex min-h-11 items-start gap-3 rounded-[var(--radius-card)] border border-border bg-surface p-4 text-text no-underline shadow-card',
        'hover:border-primary hover:bg-primary-soft',
        className,
      )}
    >
      {icon && (
        <span aria-hidden="true" className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
          {icon}
        </span>
      )}
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <H className="text-lg text-primary underline-offset-4 group-hover:underline">{title}</H>
        {description && <span className="text-muted">{description}</span>}
        {meta}
      </span>
      <ChevronRight aria-hidden="true" className="mt-1 h-5 w-5 shrink-0 text-muted" />
    </RouterLink>
  );
}

export interface StatCardProps {
  label: ReactNode;
  value: ReactNode;
  /** Plain-English line under the value, e.g. "Why this matters: …". */
  hint?: ReactNode;
  icon?: ReactNode;
  /** Slot next to the label, typically an <InfoButton ruleId=… />. */
  info?: ReactNode;
  footer?: ReactNode;
  tone?: 'default' | 'primary';
  className?: string;
}

/** A single headline figure (e.g. "Annual exemption left"). Put `<InfoButton>` in `info`. */
export function StatCard({ label, value, hint, icon, info, footer, tone = 'default', className }: StatCardProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-1 rounded-[var(--radius-card)] border p-4 shadow-card',
        tone === 'primary' ? 'border-primary bg-primary-soft' : 'border-border bg-surface',
        className,
      )}
    >
      <div className="flex items-center gap-2 text-muted">
        {icon && <span aria-hidden="true">{icon}</span>}
        <span className="font-bold">{label}</span>
        {info}
      </div>
      <div className="text-2xl font-bold text-text tabular-nums">{value}</div>
      {hint && <p className="text-muted">{hint}</p>}
      {footer && <div className="mt-2">{footer}</div>}
    </div>
  );
}
